import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, Linkedin } from "lucide-react";
import { CONTACT } from "../data/scan";
import { useNewsletterSubscribe } from "../lib/useNewsletterSubscribe";

// Pages where the full Operational Brief signup section appears immediately
// before the footer — hide any duplicate signup control in the footer.
const HAS_FULL_SIGNUP_ABOVE = (pathname) =>
  pathname === "/insights" || pathname.startsWith("/insights/");

const FooterSignupForm = () => {
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const { state, error, submit, reset } = useNewsletterSubscribe();
  const isSending = state === "sending";
  const isSuccess = state === "success";

  const onChange = (setter) => (e) => {
    if (state === "error") reset();
    setter(e.target.value);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    await submit({ email, website });
  };

  if (isSuccess) {
    return (
      <div
        data-testid="footer-signup-success"
        role="status"
        aria-live="polite"
        className="footer-signup__status border-l-2 border-terra pl-5"
      >
        <p className="text-sm font-semibold text-terra">
          Check your inbox
        </p>
        <p className="mt-3 max-w-md text-[13px] leading-relaxed text-paper/65">
          We have sent an email to confirm your subscription to The Operational Brief. Your
          subscription will become active after you select the confirmation link.
        </p>
      </div>
    );
  }

  return (
    <form
      data-testid="footer-signup-form"
      className="footer-signup relative"
      onSubmit={onSubmit}
      noValidate
    >
      <label
        htmlFor="footer-signup-email"
        className="footer-signup__label block font-display text-2xl leading-tight text-paper md:text-[1.75rem]"
      >
        Subscribe to The Operational Brief
      </label>
      <div className="footer-signup__fields mt-6 flex gap-3">
        <input
          id="footer-signup-email"
          data-testid="footer-signup-email"
          type="email"
          placeholder="Work email"
          value={email}
          onChange={onChange(setEmail)}
          disabled={isSending}
          aria-invalid={state === "error"}
          aria-describedby={state === "error" ? "footer-signup-error" : undefined}
          aria-label="Work email"
          className="min-w-0 flex-1 border-b border-paper/30 bg-transparent py-3 text-sm text-paper outline-none placeholder:text-paper/35 transition-colors focus:border-terra disabled:cursor-not-allowed disabled:opacity-60"
        />
        <button
          type="submit"
          data-testid="footer-signup-submit"
          disabled={isSending}
          className="inline-flex shrink-0 items-center border border-paper/25 px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-paper transition-colors duration-300 hover:border-terra hover:bg-terra disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isSending ? "Sending..." : "Subscribe"}
        </button>
      </div>
      <div aria-hidden="true" className="absolute -left-[9999px] w-px h-px overflow-hidden">
        <label>
          Website
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            data-testid="footer-signup-website"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </label>
      </div>
      <p className="mt-5 max-w-lg text-[11px] leading-relaxed text-paper/45">
        By subscribing, you’ll receive The Operational Brief and occasional relevant updates. You
        can unsubscribe at any time.{" "}
        <Link
          to="/privacy-policy"
          data-testid="footer-signup-privacy-link"
          className="link-rule underline underline-offset-2"
        >
          Privacy Policy
        </Link>
      </p>
      {state === "error" && (
        <p
          id="footer-signup-error"
          data-testid="footer-signup-error"
          role="alert"
          className="mt-3 text-[12px] font-medium text-terra"
        >
          {error}
        </p>
      )}
    </form>
  );
};

export const Footer = () => {
  const { pathname } = useLocation();
  const showCompactSignup = !HAS_FULL_SIGNUP_ABOVE(pathname);

  return (
    <footer data-testid="site-footer" className="site-footer bg-ink text-paper">
      <div className="mx-auto max-w-6xl px-6 pb-8 pt-16 md:px-12 md:pb-10 md:pt-24">
        <div className="footer-masthead grid gap-8 border-b border-paper/15 pb-12 md:grid-cols-12 md:items-end md:pb-16">
          <div className="md:col-span-4">
            <img
              src="/assets/logo-horizontal-reverse.png"
              alt="BOS — Behaviour Operating System"
              width="1834"
              height="444"
              loading="lazy"
              decoding="async"
              className="h-auto w-[12rem] max-w-full"
            />
          </div>
          <p className="max-w-2xl font-display text-2xl leading-snug text-paper/85 md:col-span-7 md:col-start-6 md:text-[2rem]">
              Helping organisations address recurring execution problems by improving the conditions around the work.
          </p>
        </div>

        <div className={`footer-main grid gap-12 py-12 md:grid-cols-12 md:gap-8 md:py-16 ${showCompactSignup ? "" : "footer-main--without-signup"}`}>
          {showCompactSignup && (
            <div className="md:col-span-6 lg:col-span-7">
              <FooterSignupForm />
            </div>
          )}

          <nav aria-label="Footer navigation" className={`${showCompactSignup ? "md:col-span-3 lg:col-span-2 lg:col-start-9" : "md:col-span-4 md:col-start-6"}`}>
            <p className="footer-heading text-sm font-semibold text-paper/55">Navigate</p>
            <ul className="mt-5 space-y-3 text-sm">
              {[
                ["/how-bos-works", "How BOS Works"],
                ["/bos-reveal", "BOS Reveal"],
                ["/operational-clarity-review", "Operational Clarity Review"],
                ["/insights", "Insights"],
                ["/executive-guide", "Executive Brief"],
                ["/about", "About"],
                ["/contact", "Contact"],
              ].map(([to, label]) => (
                <li key={to}>
                  <Link
                    to={to}
                    data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="link-rule text-paper/68 transition-colors duration-300 hover:text-paper"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
          <div className="md:col-span-3 lg:col-span-2">
            <p className="footer-heading text-sm font-semibold text-paper/55">Contact</p>
            <ul className="mt-5 space-y-3 text-sm text-paper/68">
              <li>{CONTACT.name}</li>
              <li>{CONTACT.location}</li>
              <li>
                <Link
                  to="/contact"
                  data-testid="footer-contact-link"
                  className="link-rule inline-flex items-center gap-1 text-paper/68 transition-colors duration-300 hover:text-paper"
                >
                  Contact BOS <ArrowUpRight size={13} />
                </Link>
              </li>
              <li>
                <a
                  data-testid="footer-linkedin-link"
                  href={CONTACT.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Connect with Tristan Todd on LinkedIn"
                  className="inline-flex items-center gap-2.5 text-paper/68 transition-colors duration-300 hover:text-paper focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-terra"
                >
                  <span>LinkedIn</span>
                  <Linkedin size={18} aria-hidden="true" />
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="footer-legal flex flex-col gap-4 border-t border-paper/15 pt-7 md:flex-row md:items-center md:justify-between">
          <p className="text-[11px] leading-relaxed text-paper/38">
            © {new Date().getFullYear()} BOS — Behaviour Operating System. Sydney, Australia.
          </p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <Link
              to="/privacy-policy"
              data-testid="footer-link-privacy"
              className="link-rule text-[11px] text-paper/45 transition-colors duration-300 hover:text-paper/75"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms-of-use"
              data-testid="footer-link-terms"
              className="link-rule text-[11px] text-paper/45 transition-colors duration-300 hover:text-paper/75"
            >
              Terms of Use
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
